/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export interface ElevenLabsVoice {
  voice_id: string;
  name: string;
  category: string;
  description: string;
  gender: string;
  age: string;
  use_case: string;
  accent: string;
  preview_url?: string;
}

export interface TextToSpeechOptions {
  text: string;
  voice_id?: string;
  model_id?: string;
  stability?: number;
  similarity_boost?: number;
  style?: number;
  speaker_boost?: boolean;
}

export interface VoiceCloningOptions {
  name: string;
  description?: string;
  files: File[];
}

export class ElevenLabsService {
  private apiKey: string;
  private baseUrl: string = 'https://api.elevenlabs.io/v1';

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'xi-api-key': this.apiKey,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  private async makeBinaryRequest(endpoint: string, options: RequestInit = {}): Promise<ArrayBuffer> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'xi-api-key': this.apiKey,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.arrayBuffer();
  }

  /**
   * Get available voices
   */
  async getVoices(): Promise<ElevenLabsVoice[]> {
    try {
      const response = await this.makeRequest<{ voices: any[] }>('/voices');
      return response.voices.map((voice: any) => ({
        voice_id: voice.voice_id,
        name: voice.name,
        category: voice.category,
        description: voice.description,
        gender: voice.gender,
        age: voice.age,
        use_case: voice.use_case,
        accent: voice.accent,
        preview_url: voice.preview_url,
      }));
    } catch (error) {
      console.error('Error fetching voices:', error);
      throw new Error('Failed to fetch voices from Eleven Labs');
    }
  }

  /**
   * Convert text to speech
   */
  async textToSpeech(options: TextToSpeechOptions): Promise<ArrayBuffer> {
    try {
      const voiceId = options.voice_id || 'rachel';
      const requestBody = {
        text: options.text,
        model_id: options.model_id || 'eleven_monolingual_v1',
        voice_settings: {
          stability: options.stability || 0.5,
          similarity_boost: options.similarity_boost || 0.5,
          style: options.style || 0.0,
          use_speaker_boost: options.speaker_boost || false,
        },
      };

      return await this.makeBinaryRequest(`/text-to-speech/${voiceId}`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
    } catch (error) {
      console.error('Error converting text to speech:', error);
      throw new Error('Failed to convert text to speech');
    }
  }

  /**
   * Stream text to speech (simulated for browser)
   */
  async streamTextToSpeech(
    options: TextToSpeechOptions,
    onChunk: (chunk: ArrayBuffer) => void
  ): Promise<void> {
    try {
      // For browser environment, we'll get the full audio and then chunk it
      const audioBuffer = await this.textToSpeech(options);
      
      // Simulate streaming by sending chunks
      const chunkSize = 8192; // 8KB chunks
      for (let i = 0; i < audioBuffer.byteLength; i += chunkSize) {
        const chunk = audioBuffer.slice(i, i + chunkSize);
        onChunk(chunk);
        // Small delay to simulate streaming
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    } catch (error) {
      console.error('Error streaming text to speech:', error);
      throw new Error('Failed to stream text to speech');
    }
  }

  /**
   * Get user information
   */
  async getUserInfo(): Promise<any> {
    try {
      return await this.makeRequest('/user');
    } catch (error) {
      console.error('Error fetching user info:', error);
      throw new Error('Failed to fetch user information');
    }
  }

  /**
   * Get subscription information
   */
  async getSubscriptionInfo(): Promise<any> {
    try {
      return await this.makeRequest('/user/subscription');
    } catch (error) {
      console.error('Error fetching subscription info:', error);
      throw new Error('Failed to fetch subscription information');
    }
  }

  /**
   * Clone a voice (simplified for browser)
   */
  async cloneVoice(options: VoiceCloningOptions): Promise<string> {
    try {
      // Step 1: Create voice
      const voiceResponse = await this.makeRequest<{ voice_id: string }>('/voices/add', {
        method: 'POST',
        body: JSON.stringify({
          name: options.name,
          description: options.description || '',
        }),
      });

      const voiceId = voiceResponse.voice_id;

      // Step 2: Upload samples (simplified - in real implementation would need FormData)
      // For browser compatibility, we'll simulate this step
      console.log('Voice samples would be uploaded here for voice:', voiceId);

      return voiceId;
    } catch (error) {
      console.error('Error cloning voice:', error);
      throw new Error('Failed to clone voice');
    }
  }

  /**
   * Delete a custom voice
   */
  async deleteVoice(voiceId: string): Promise<void> {
    try {
      await this.makeRequest(`/voices/${voiceId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting voice:', error);
      throw new Error('Failed to delete voice');
    }
  }

  /**
   * Get voice settings
   */
  async getVoiceSettings(voiceId: string): Promise<any> {
    try {
      return await this.makeRequest(`/voices/${voiceId}/settings`);
    } catch (error) {
      console.error('Error fetching voice settings:', error);
      throw new Error('Failed to fetch voice settings');
    }
  }

  /**
   * Update voice settings
   */
  async updateVoiceSettings(voiceId: string, settings: any): Promise<void> {
    try {
      await this.makeRequest(`/voices/${voiceId}/settings`, {
        method: 'POST',
        body: JSON.stringify(settings),
      });
    } catch (error) {
      console.error('Error updating voice settings:', error);
      throw new Error('Failed to update voice settings');
    }
  }

  /**
   * Get audio history
   */
  async getAudioHistory(): Promise<any> {
    try {
      return await this.makeRequest('/history');
    } catch (error) {
      console.error('Error fetching audio history:', error);
      throw new Error('Failed to fetch audio history');
    }
  }

  /**
   * Delete audio from history
   */
  async deleteAudioFromHistory(audioId: string): Promise<void> {
    try {
      await this.makeRequest(`/history/${audioId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting audio from history:', error);
      throw new Error('Failed to delete audio from history');
    }
  }

  /**
   * Get voice samples (for preview)
   */
  async getVoiceSamples(voiceId: string): Promise<any> {
    try {
      return await this.makeRequest(`/voices/${voiceId}/samples`);
    } catch (error) {
      console.error('Error fetching voice samples:', error);
      throw new Error('Failed to fetch voice samples');
    }
  }
}

// Singleton instance
let elevenLabsService: ElevenLabsService | null = null;

export function getElevenLabsService(apiKey: string): ElevenLabsService {
  if (!elevenLabsService || elevenLabsService['apiKey'] !== apiKey) {
    elevenLabsService = new ElevenLabsService(apiKey);
  }
  return elevenLabsService;
}
